import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import LogoBlock from './LogoBlock';
import { NotificationBell } from '../notifications/NotificationBell';
import { Icon } from '../common/Icon';

const adminNavItems = [
  { icon: 'navigation.dashboard', label: 'Dashboard', href: '/admin', adminOnly: true },

  // ── Lead operations ──
  { section: 'Leads' },
  { icon: 'admin.users', label: 'Tester Campaigns', href: '/admin/testers', adminOnly: true },
  { icon: 'phone-call', label: 'Lead Acquisition', href: '/admin/leads', adminOnly: true },
  { icon: 'bot', label: 'AI Call Center', href: '/admin/campaigns', adminOnly: true },
  { icon: 'search-check', label: 'Lead Sources', href: '/admin/lead-sources', adminOnly: true },

  // ── Provider management ──
  { section: 'Providers' },
  { icon: 'building-2', label: 'Providers', href: '/admin/providers', adminOnly: true },
  { icon: 'badge-check', label: 'Provider Claims', href: '/admin/provider-claims', adminOnly: true },
  { icon: 'shield-check', label: 'KYC & Verification', href: '/admin/kyc' },
  { icon: 'shield-alert', label: 'KYC Expiry & Renewal', href: '/admin/kyc-expiry', adminOnly: true },
  { icon: 'file-text', label: 'Requests', href: '/admin/requests', adminOnly: true },
  { icon: 'admin.users', label: 'Users', href: '/admin/users', adminOnly: true },

  // ── Support (UAT-6) ──
  { section: 'Support' },
  { icon: 'support.help', label: 'Support Inbox', href: '/admin/support', adminOnly: true },

  // ── Monetization (Phase M1 — canonical) ──
  { section: 'Monetization' },
  { icon: 'pie-chart', label: 'Billing Overview', href: '/admin/monetization/overview', adminOnly: true },
  { icon: 'billing.plan', label: 'Provider Plans', href: '/admin/monetization/plans', adminOnly: true },
  { icon: 'package', label: 'Customer Products', href: '/admin/monetization/customer-products', adminOnly: true },
  { icon: 'billing.payment', label: 'Customer Billing', href: '/admin/monetization/customer-billing', adminOnly: true },
  { icon: 'billing.payment', label: 'Subscriptions', href: '/admin/monetization/subscriptions', adminOnly: true },
  { icon: 'bar-chart-3', label: 'Usage & Limits', href: '/admin/monetization/usage', adminOnly: true },
  // M2-B: Billing Orders / Invoices / Credit Notes (read-only)
  { icon: 'billing.cart', label: 'Billing Orders', href: '/admin/monetization/orders', adminOnly: true },
  { icon: 'billing.invoice', label: 'Invoices', href: '/admin/monetization/invoices', adminOnly: true },
  { icon: 'file-minus', label: 'Credit Notes', href: '/admin/monetization/credit-notes', adminOnly: true },
  { icon: 'support.mail', label: 'Email Logs', href: '/admin/monetization/email-logs', adminOnly: true },

  // ── Legacy monetization routes (/admin/pricing-plans, /admin/subscriptions)
  // were retired in Phase P2 and now redirect to the canonical Monetization
  // section above. The page modules have been deleted from the codebase.

  // ── Catalog ──
  { section: 'Catalog' },
  { icon: 'globe', label: 'Currencies', href: '/admin/currencies', adminOnly: true },
  { icon: 'admin.database', label: 'Master Data', href: '/admin/master-data', adminOnly: true },
  { icon: 'scan-line', label: 'VIN Decode', href: '/admin/vin-decode', adminOnly: true },
  { icon: 'admin.integrations', label: 'Vehicle APIs', href: '/admin/settings/vehicle-apis', adminOnly: true },
  { icon: 'admin.activity', label: 'Vehicle Status', href: '/admin/vehicle-system-status', adminOnly: true },

  // ── Content / comms ──
  { section: 'Content & communications' },
  { icon: 'file-text', label: 'Content Pages', href: '/admin/content-pages', adminOnly: true },
  { icon: 'navigation.menu', label: 'Menu Manager', href: '/admin/menu-manager', adminOnly: true },
  { icon: 'type', label: 'UI Text', href: '/admin/ui-text', adminOnly: true },
  { icon: 'navigation.search', label: 'SEO Meta', href: '/admin/seo-meta', adminOnly: true },
  { icon: 'support.mail', label: 'Email Logs', href: '/admin/email-logs', adminOnly: true },

  // ── System ──
  { section: 'System' },
  { icon: 'clipboard-list', label: 'Audit Logs', href: '/admin/audit-logs', adminOnly: true },
  { icon: 'blocks', label: 'Integrations', href: '/admin/integrations', adminOnly: true },
  { icon: 'notifications.bell', label: 'Firebase Push', href: '/admin/integrations/firebase', adminOnly: true },
  { icon: 'image', label: 'Media Uploads', href: '/admin/media-uploads', adminOnly: true },
  { icon: 'admin.settings', label: 'System Settings', href: '/admin/system-settings', adminOnly: true },
];

function SidebarContent({ visibleNav, user, onLogout, onNavigate }) {
  return (
    <div className="h-full flex flex-col">
      {/* Brand header */}
      <div className="p-4 border-b">
        <LogoBlock
          variant="compact"
          subtitle="Admin Panel"
          to="/admin"
          onClick={onNavigate}
          testId="admin-logo"
        />
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="space-y-1 px-2" data-testid="admin-nav">
          {visibleNav.map((item, idx) => {
            if (item.section) {
              return (
                <div
                  key={`section-${idx}`}
                  className="pt-3 pb-1 px-3 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium"
                >
                  {item.section}
                </div>
              );
            }
            return (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href === '/admin'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `ds-nav-link flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'ds-nav-active bg-accent text-white'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`
                }
                data-testid={`nav-${item.href.replace(/\//g, '-')}`}
              >
                <Icon name={item.icon} className="h-4 w-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User section */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
            <span className="text-sm font-medium text-accent">
              {user?.name?.charAt(0) || 'A'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" data-testid="admin-user-name">
              {user?.name || 'Admin'}
            </p>
            <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onLogout}
          data-testid="admin-logout-btn"
        >
          <Icon name="log-out" className="h-4 w-4 me-2" />
          Logout
        </Button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isModerator = user?.role === 'moderator';
  const visibleNav = isModerator
    ? adminNavItems.filter((i) => i.section || !i.adminOnly)
    : adminNavItems;

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Scroll lock when drawer open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="admin-layout">
      {/* Mobile topbar — only visible below md breakpoint */}
      <header className="md:hidden sticky top-0 z-30 flex h-14 items-center justify-between border-b bg-card px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(true)}
          aria-label="Open admin menu"
          data-testid="admin-mobile-toggle"
        >
          <Icon name="navigation.menu" className="h-5 w-5" />
        </Button>
        <LogoBlock variant="drawer" to="/admin" testId="admin-topbar-logo" />
        <NotificationBell />
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar — pinned, always visible on md+ */}
        <aside
          className="hidden md:flex w-64 border-e bg-card flex-col shrink-0"
          data-testid="admin-sidebar-desktop"
        >
          <SidebarContent
            visibleNav={visibleNav}
            user={user}
            onLogout={handleLogout}
          />
        </aside>

        {/* Mobile drawer + overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={closeMobile}
            data-testid="admin-mobile-overlay"
          />
        )}
        <aside
          className={`fixed top-0 start-0 z-50 h-full w-72 border-e bg-card shadow-xl transform transition-transform duration-300 ease-in-out md:hidden ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          data-testid="admin-sidebar-mobile"
        >
          <div className="flex items-center justify-end p-2 border-b">
            <Button
              variant="ghost"
              size="icon"
              onClick={closeMobile}
              aria-label="Close admin menu"
              data-testid="admin-mobile-close"
            >
              <Icon name="x" className="h-5 w-5" />
            </Button>
          </div>
          <SidebarContent
            visibleNav={visibleNav}
            user={user}
            onLogout={handleLogout}
            onNavigate={closeMobile}
          />
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col overflow-hidden" data-testid="admin-main">
          {/* Desktop top action bar — houses notification bell so admins see live alerts.
              Mobile gets the bell via the topbar above. */}
          <div className="hidden md:flex h-14 items-center justify-end gap-2 px-6 border-b bg-card" data-testid="admin-desktop-topbar">
            <NotificationBell />
          </div>
          <ScrollArea className="flex-1">
            <div className="p-4 sm:p-6">
              <Outlet />
            </div>
          </ScrollArea>
        </main>
      </div>
    </div>
  );
}
